import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import PageHead from "@/components/PageHead";
import { blogPosts } from "@/lib/blog-posts";
import { useLocale } from "@/hooks/use-locale";
import { useScrollAnimation } from "@/hooks/use-scroll-animation";
import { trackCTAClick } from "@/lib/analytics";

const Blog = () => {
  const { t } = useTranslation();
  const { prefix } = useLocale();
  const [gridRef, gridVisible] = useScrollAnimation<HTMLDivElement>();

  return (
    <>
      <PageHead
        title={t("blog.seoTitle")}
        description={t("blog.seoDesc")}
        canonical="https://queebi.com/blog"
        image="/images/og/blog-1200x630.jpg"
        imageAlt="Notes from Queebi Land — parenting insights and emotional learning tips"
        keywords="Queebi blog, parenting tips, baby products, newborn gifts, pacifier tips, comforter for babies, emotional learning, baby sleep tips, nursery essentials, baby shower ideas"
      />

      <section className="bg-background py-20 md:py-32">
        <div className="container max-w-5xl">
          <h1 className="text-4xl md:text-5xl font-bold font-heading text-center mb-4">{t("blog.title")}</h1>
          <p className="text-muted-foreground text-lg text-center mb-14 max-w-2xl mx-auto">{t("blog.subtitle")}</p>

          <div
            ref={gridRef}
            className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 transition-all duration-700 ${gridVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
          >
            {blogPosts.map((post) => (
              <Link
                key={post.slug}
                to={`${prefix}/blog/${post.slug}`}
                onClick={() => trackCTAClick(`blog_${post.slug}`)}
                className="group rounded-2xl overflow-hidden bg-card shadow-sm hover:shadow-lg transition-all duration-300 hover:-translate-y-1"
              >
                <div className="aspect-[4/3] overflow-hidden">
                  <img src={post.image} alt={post.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" width={400} height={300} loading="lazy" />
                </div>
                <div className="p-5">
                  <p className="text-sm text-muted-foreground mb-2">{post.date}</p>
                  <h3 className="font-bold text-base leading-snug">{post.title}</h3>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ItemList",
            name: "Queebi Blog",
            itemListElement: blogPosts.map((post, i) => ({
              "@type": "ListItem",
              position: i + 1,
              item: {
                "@type": "BlogPosting",
                headline: post.title,
                url: `https://queebi.com/blog/${post.slug}`,
                image: `https://queebi.com${post.image}`,
                datePublished: post.dateISO,
                author: { "@type": "Person", name: post.author },
              },
            })),
          }),
        }}
      />
    </>
  );
};

export default Blog;
